import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  TOPIC_STATUS_LABELS,
  TOPIC_STATUSES,
  formatMinutes,
  type TopicStatus,
  type TopicView,
} from '@algo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { Markdown } from '../components/Markdown';
import {
  Badge,
  Button,
  Card,
  ErrorState,
  Field,
  Input,
  Modal,
  PageHeader,
  Select,
  Spinner,
  Textarea,
} from '../components/ui';
import { INVALIDATE_ON_WRITE, api, qk } from '../lib/api';

const STATUS_TONE: Record<TopicStatus, 'neutral' | 'accent' | 'warning' | 'success'> = {
  NOT_STARTED: 'neutral',
  LEARNING: 'warning',
  PRACTICING: 'accent',
  MASTERED: 'success',
};

function ConfidenceDots({ value }: { value: number }) {
  return (
    <span className="inline-flex gap-0.5" title={`Итгэл ${value}/5`}>
      {[1, 2, 3, 4, 5].map((step) => (
        <span
          key={step}
          className={clsx(
            'h-1.5 w-1.5 rounded-full',
            step <= value ? 'bg-accent' : 'bg-edge',
          )}
        />
      ))}
    </span>
  );
}

function TopicDetail({ topic, onClose }: { topic: TopicView; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<TopicStatus>(topic.status);
  const [confidence, setConfidence] = useState(topic.confidence);
  const [notes, setNotes] = useState(topic.notes);
  const [resourceTitle, setResourceTitle] = useState('');
  const [resourceUrl, setResourceUrl] = useState('');

  const invalidate = async () => {
    await Promise.all(
      [...INVALIDATE_ON_WRITE, qk.topic(topic.id)].map((key) =>
        queryClient.invalidateQueries({ queryKey: key as readonly unknown[] }),
      ),
    );
  };

  const save = useMutation({
    mutationFn: () => api.updateTopic(topic.id, { status, confidence, notes }),
    onSuccess: async () => {
      await invalidate();
      onClose();
    },
  });

  const addResource = useMutation({
    mutationFn: () => api.addResource(topic.id, { title: resourceTitle, url: resourceUrl }),
    onSuccess: async () => {
      setResourceTitle('');
      setResourceUrl('');
      await invalidate();
    },
  });

  const removeResource = useMutation({
    mutationFn: (id: string) => api.deleteResource(id),
    onSuccess: invalidate,
  });

  return (
    <Modal open title={topic.name} onClose={onClose} wide>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Төлөв">
          <Select value={status} onChange={(event) => setStatus(event.target.value as TopicStatus)}>
            {TOPIC_STATUSES.map((option) => (
              <option key={option} value={option}>
                {TOPIC_STATUS_LABELS[option]}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={`Итгэл — ${confidence}/5`} hint="1 = огт мэдэхгүй · 5 = бусдад заах чадвартай">
          <input
            type="range"
            min={1}
            max={5}
            step={1}
            value={confidence}
            onChange={(event) => setConfidence(Number(event.target.value))}
            className="w-full accent-[rgb(var(--accent))]"
          />
        </Field>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3 text-center">
        {[
          { label: 'Бодсон бодлого', value: topic.problemsSolved },
          { label: 'Сүүлийн 30 хоног', value: topic.problemsSolvedLast30Days },
          { label: 'Судалсан хугацаа', value: formatMinutes(topic.minutesStudied) },
        ].map((stat) => (
          <div key={stat.label} className="rounded-lg border border-edge bg-raised p-3">
            <p className="font-mono text-lg">{stat.value}</p>
            <p className="text-xs text-muted">{stat.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-5">
        <Field label="Тэмдэглэл (markdown)">
          <Textarea
            rows={8}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Гол санаа, загвар код, анхаарах зүйлс…"
          />
        </Field>
        {notes.trim() && (
          <details className="mt-2">
            <summary className="cursor-pointer text-xs text-muted">Урьдчилан харах</summary>
            <div className="mt-2 rounded-lg border border-edge bg-raised p-4">
              <Markdown>{notes}</Markdown>
            </div>
          </details>
        )}
      </div>

      <div className="mt-5">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">Эх сурвалж</p>
        <ul className="space-y-1.5">
          {topic.resources.map((resource) => (
            <li
              key={resource.id}
              className="flex items-center justify-between gap-2 rounded-lg border border-edge bg-raised px-3 py-2"
            >
              <a
                href={resource.url}
                target="_blank"
                rel="noreferrer"
                className="truncate text-sm text-accent underline underline-offset-2"
              >
                {resource.title}
              </a>
              <Button
                variant="ghost"
                onClick={() => removeResource.mutate(resource.id)}
                aria-label={`${resource.title}-г устгах`}
              >
                ✕
              </Button>
            </li>
          ))}
          {topic.resources.length === 0 && <li className="text-sm text-muted">Одоохондоо алга.</li>}
        </ul>

        <div className="mt-3 flex flex-wrap gap-2">
          <Input
            className="min-w-[10rem] flex-1"
            placeholder="Гарчиг"
            value={resourceTitle}
            onChange={(event) => setResourceTitle(event.target.value)}
          />
          <Input
            className="min-w-[14rem] flex-[2]"
            placeholder="https://…"
            value={resourceUrl}
            onChange={(event) => setResourceUrl(event.target.value)}
          />
          <Button
            disabled={!resourceTitle.trim() || !resourceUrl.trim() || addResource.isPending}
            onClick={() => addResource.mutate()}
          >
            Нэмэх
          </Button>
        </div>
        {addResource.error && (
          <p className="mt-2 text-xs text-danger">
            {(addResource.error as Error).message}
          </p>
        )}
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          Болих
        </Button>
        <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Хадгалж байна…' : 'Сэдвийг хадгалах'}
        </Button>
      </div>
    </Modal>
  );
}

export function RoadmapPage() {
  const [selected, setSelected] = useState<string | null>(null);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: qk.topics,
    queryFn: () => api.listTopics(),
  });

  if (isLoading) return <Spinner label="Сургалтын замыг ачаалж байна…" />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!data) return null;

  const selectedTopic = data.find((topic) => topic.id === selected) ?? null;
  const mastered = data.filter((topic) => topic.status === 'MASTERED').length;

  return (
    <>
      <PageHeader
        title="Сургалтын зам"
        description={`${data.length} сэдвээс ${mastered}-г эзэмшсэн · төлөв, итгэл, тэмдэглэл, эх сурвалжаа тохируулахын тулд сэдэв дээр дар`}
      />

      <div className="space-y-6">
        {CATEGORY_ORDER.map((category) => {
          const topics = data.filter((topic) => topic.category === category);
          if (topics.length === 0) return null;
          const done = topics.filter((topic) => topic.status === 'MASTERED').length;

          return (
            <Card
              key={category}
              title={CATEGORY_LABELS[category]}
              subtitle={`${done}/${topics.length} эзэмшсэн`}
              bodyClassName="p-3"
            >
              <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {topics.map((topic) => (
                  <button
                    key={topic.id}
                    type="button"
                    onClick={() => setSelected(topic.id)}
                    className="flex flex-col gap-2 rounded-lg border border-edge bg-raised p-3 text-left transition hover:border-accent"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-sm font-medium">{topic.name}</span>
                      <Badge tone={STATUS_TONE[topic.status]}>{TOPIC_STATUS_LABELS[topic.status]}</Badge>
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted">
                      <span>
                        {topic.problemsSolved} бодолт · {formatMinutes(topic.minutesStudied)}
                      </span>
                      <ConfidenceDots value={topic.confidence} />
                    </div>
                  </button>
                ))}
              </div>
            </Card>
          );
        })}
      </div>

      {selectedTopic && (
        <TopicDetail topic={selectedTopic} onClose={() => setSelected(null)} />
      )}
    </>
  );
}
